import { Alert, AlertDescription, AlertTitle, Button, Spinner } from '@folio/components';
import { cn } from '@folio/lib';
import { AlertCircle, Maximize2, Minimize2, Minus, Plus, RotateCcw, RotateCw } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TransformComponent, TransformWrapper, useControls } from 'react-zoom-pan-pinch';
import { Center, Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

interface ImageViewerProps {
  url: string;
  alt: string;
  className?: string;
}

function ToolbarButton({
  onClick,
  label,
  children,
}: {
  onClick: () => void;
  label: string;
  children: ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={onClick}
      className="h-auto w-auto min-h-11 min-w-11 md:min-h-0 md:min-w-0 p-1.5 text-slate-600"
      aria-label={label}
    >
      {children}
    </Button>
  );
}

function ZoomControls({
  scale,
  rotation,
  onRotate,
  onReset,
  isFullscreen,
  onToggleFullscreen,
}: {
  scale: number;
  rotation: number;
  onRotate: () => void;
  onReset: () => void;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
}) {
  const { t } = useTranslation();
  const { zoomIn, zoomOut } = useControls();
  const zoomPercent = Math.round(scale * 100);
  const isZoomed = Math.abs(scale - 1) > 0.01 || rotation !== 0;

  return (
    <Group
      gap={0}
      className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 gap-0.5 rounded-2xl border border-slate-200/60 bg-white/95 px-2 py-1 shadow-lg backdrop-blur-sm"
    >
      <ToolbarButton onClick={() => zoomOut()} label={t('documentReview.zoomOut')}>
        <Minus className="w-4 h-4" />
      </ToolbarButton>
      <Text inline size="xs" className="tabular-nums w-10 text-center select-none text-slate-500">
        {zoomPercent}%
      </Text>
      <ToolbarButton onClick={() => zoomIn()} label={t('documentReview.zoomIn')}>
        <Plus className="w-4 h-4" />
      </ToolbarButton>
      <div className="mx-0.5 h-5 w-px bg-slate-200" aria-hidden="true" />
      <ToolbarButton onClick={onRotate} label={t('documentReview.rotate')}>
        <RotateCw className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={onToggleFullscreen}
        label={t(isFullscreen ? 'documentReview.exitFullscreen' : 'documentReview.fullscreen')}
      >
        {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
      </ToolbarButton>
      {isZoomed && (
        <>
          <div className="mx-0.5 h-5 w-px bg-slate-200" aria-hidden="true" />
          <ToolbarButton onClick={onReset} label={t('documentReview.resetZoom')}>
            <RotateCcw className="w-3.5 h-3.5" />
          </ToolbarButton>
        </>
      )}
    </Group>
  );
}

export function ImageViewer({ url, alt, className }: ImageViewerProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const resetRef = useRef<(() => void) | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(true);
    setError(false);
    setScale(1);
    setRotation(0);
    resetRef.current?.();
  }, [url]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(window.document.fullscreenElement === containerRef.current);
    };
    window.document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => window.document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleLoad = () => {
    setLoading(false);
    setError(false);
  };

  const handleError = () => {
    setLoading(false);
    setError(true);
  };

  const handleRotate = () => setRotation(r => (r + 90) % 360);

  const handleReset = () => {
    resetRef.current?.();
    setRotation(0);
  };

  const handleToggleFullscreen = () => {
    if (window.document.fullscreenElement) {
      void window.document.exitFullscreen();
    } else {
      void containerRef.current?.requestFullscreen();
    }
  };

  return (
    <Stack
      gap={0}
      ref={containerRef}
      className={cn('relative h-full w-full bg-slate-50', className)}
    >
      {loading && (
        <Stack align="center" justify="center" className="min-h-[250px] md:min-h-[400px]">
          <Spinner className="h-8 w-8" />
          <Text size="sm" tone="muted">
            {t('documents.viewer.image.loading')}
          </Text>
        </Stack>
      )}

      {error && (
        <Center className="min-h-[250px] md:min-h-[400px]">
          <Alert variant="destructive" className="max-w-md">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('documents.viewer.image.errorTitle')}</AlertTitle>
            <AlertDescription>{t('documents.viewer.image.errorDescription')}</AlertDescription>
          </Alert>
        </Center>
      )}

      <TransformWrapper
        initialScale={1}
        minScale={0.5}
        maxScale={5}
        centerOnInit
        wheel={{ step: 0.15 }}
        pinch={{ step: 100 }}
        doubleClick={{ mode: 'reset' }}
        onInit={ref => {
          resetRef.current = () => ref.resetTransform(0);
        }}
        onTransformed={(_ref, state) => {
          setScale(state.scale);
        }}
      >
        <div className={cn('flex-1 relative', loading || error ? 'hidden' : 'block')}>
          <ZoomControls
            scale={scale}
            rotation={rotation}
            onRotate={handleRotate}
            onReset={handleReset}
            isFullscreen={isFullscreen}
            onToggleFullscreen={handleToggleFullscreen}
          />
          <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }}>
            <img
              src={url}
              alt={alt}
              onLoad={handleLoad}
              onError={handleError}
              draggable={false}
              style={{ transform: `rotate(${rotation}deg)` }}
              className="max-w-full max-h-[calc(100vh-220px)] h-auto object-contain select-none transition-transform duration-200"
            />
          </TransformComponent>
        </div>
      </TransformWrapper>
    </Stack>
  );
}
